import { AlertTriangle, Check, CircleSlash, Clock, ShieldAlert } from "lucide-react";
import { cn } from "./cn";

export type GateState = "PASS" | "FAIL" | "WAITING" | "NOT_APPLICABLE";

/**
 * One readiness gate's verdict, shown as its own thing rather than folded into a score.
 *
 * <p>The waived case is why this is a component and not a StatusBadge with a colour prop. A gate
 * that failed and was formally waived must go on reading FAIL — that is the whole of §23 — while
 * also showing that somebody accepted the risk. Two facts, one row, and neither allowed to erase
 * the other.
 *
 * <p>NOT_APPLICABLE is drawn as plainly as possible on purpose. It is not a pass, and anything
 * green here would read as reassurance nobody earned.
 */
const STATES: Record<GateState, { tone: string; icon: typeof Check; label: string }> = {
  PASS: { tone: "text-emerald-700 bg-emerald-50 ring-emerald-600/20", icon: Check, label: "Pass" },
  FAIL: { tone: "text-red-700 bg-red-50 ring-red-600/20", icon: AlertTriangle, label: "Fail" },
  WAITING: { tone: "text-amber-700 bg-amber-50 ring-amber-600/20", icon: Clock, label: "Waiting" },
  NOT_APPLICABLE: {
    tone: "text-ink-500 bg-ink-50 ring-ink-400/20",
    icon: CircleSlash,
    label: "Not applicable",
  },
};

export function GateBadge({ state, waived }: { state: GateState; waived?: boolean }) {
  const { tone, icon: Icon, label } = STATES[state];

  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
          tone,
        )}
      >
        <Icon className="h-3 w-3" aria-hidden />
        {label}
      </span>
      {waived && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 ring-1 ring-inset ring-violet-600/20"
          title="This gate failed and the risk was formally accepted"
        >
          <ShieldAlert className="h-3 w-3" aria-hidden />
          Waived
        </span>
      )}
    </span>
  );
}
